import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { authorizedAxios, useAuth } from "@moonscape/shared";

type UseUkIncomeStatementOptions = {
  taxYear: number;
  enabled?: boolean;
};

export type UkIncomeStatementRow = {
  id: number | null;
  datetime: string | null;
  type: string;
  dataSource: string;
  account: string;
  direction: "inflow";
  quantity: number;
  asset: string;
  valueGBP: number;
  feesGBP: number;
  extTxnId: string | null;
};

export type UkIncomeStatementResponse = {
  taxYear: number;
  taxSetting: any;
  currency: string;
  totals: {
    totalValueGBP: number;
    totalEvents: number;
  };
  rows: UkIncomeStatementRow[];
};

export const useUkIncomeStatement = ({
  taxYear,
  enabled = true,
}: UseUkIncomeStatementOptions): UseQueryResult<UkIncomeStatementResponse | null, Error> => {
  const { getToken, user } = useAuth();

  const fetchIncomeStatement = async (): Promise<UkIncomeStatementResponse | null> => {
    const token = await getToken();
    try {
      const response = await authorizedAxios(token).get(
        "/export/tax-report/uk/income-statement",
        {
          params: { year: taxYear },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        return null;
      }

      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const code = error?.response?.data?.error;
      if (status === 400 && code === "unsupported_jurisdiction") {
        return null;
      }
      throw error;
    }
  };

  return useQuery({
    queryKey: ["uk_income_statement", taxYear],
    queryFn: fetchIncomeStatement,
    enabled: enabled && !!user?.email && Number.isFinite(taxYear),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
